import {Grid} from "@mui/material";
import {CopyUtils} from "../../../util/CopyUtils";
import {FormSessionTranslKey} from "../FormSessionTranslKey";
import {useTranslation} from "react-i18next";
import {FormSessionFilterDto} from "../../../client/formsession/FormSessionApiClient";
import TextField from "../../../common/form/TextField";
import MultiSelect, {MultiSelectOption} from "../../../common/form/MultiSelect";
import DateField from "../../../common/form/DateField";
import {DateTimeFormatter} from "../../../common/DateTimeFormatter";
import {useCatalog} from "../../../common/catalog/UseCatalog";
import {CatalogType} from "../../../client/catalog/CatalogType";
import {DocumentType} from "../../../client/submission/SubmissionApiClient";

export interface FormSessionFilterFormProps {
    filter: FormSessionFilterDto;
    onChange: (filter: FormSessionFilterDto) => void;
}

export function FormSessionFilterForm(props: Readonly<FormSessionFilterFormProps>) {
    const {filter, onChange} = props;

    const {t} = useTranslation();
    const {catalogValues: documentTypeValues} = useCatalog(CatalogType.DOCUMENT_TYPE);

    function onAccessedFromChange(accessedFrom: Date | undefined) {
        const updatedFilter = CopyUtils.deepCopy(filter);
        updatedFilter.createdAtFrom = DateTimeFormatter.convertDateToIsoFormat(accessedFrom);
        onChange(updatedFilter);
    }

    function onAccessedThruChange(accessedThru: Date | undefined) {
        const updatedFilter = CopyUtils.deepCopy(filter);
        const isoDateAccessedThru = DateTimeFormatter.convertDateToIsoFormat(accessedThru);
        updatedFilter.createdAtTo = DateTimeFormatter.formatAsIsoDateTimeAtEndOfDay(isoDateAccessedThru);
        onChange(updatedFilter);
    }

    function onDocumentTypesChange(documentTypes: string[]) {
        const updatedFilter = CopyUtils.deepCopy(filter);
        updatedFilter.documentTypes = documentTypes.map(value => value);
        onChange(updatedFilter);
    }

    function onUserNameChange(userName: string) {
        const updatedFilter = CopyUtils.deepCopy(filter);
        updatedFilter.person = updatedFilter.person ?? {};
        updatedFilter.person.nameLike = userName;
        onChange(updatedFilter);
    }

    function onEmailChange(email: string) {
        const updatedFilter = CopyUtils.deepCopy(filter);
        updatedFilter.person = updatedFilter.person ?? {};
        updatedFilter.person.emailLike = email;
        onChange(updatedFilter);
    }

    function getDocumentTypeOptions(selectedDocumentTypes?: string[]): MultiSelectOption[] {
        const isSpecialType = (code: string) =>
            code === DocumentType.NOT_DETECTED_YET || code === DocumentType.UNKNOWN;

        return documentTypeValues
            .filter(type => !isSpecialType(type.code))
            .map(catalogValue => ({
                value: catalogValue.code,
                label: t(catalogValue.code, {ns: CatalogType.DOCUMENT_TYPE}),
                selected: selectedDocumentTypes?.includes(catalogValue.code),
                disabled: false
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    return (
        <Grid container mt={0} spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
                <DateField label={t(FormSessionTranslKey.ACCESSED_FROM)}
                           value={DateTimeFormatter.convertFromIsoDateTimeString(filter.createdAtFrom)}
                           onChange={onAccessedFromChange}/>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <DateField label={t(FormSessionTranslKey.ACCESSED_THRU)}
                           value={DateTimeFormatter.convertFromIsoDateTimeString(filter.createdAtTo)}
                           onChange={onAccessedThruChange}/>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <MultiSelect label={t(FormSessionTranslKey.DOCUMENT_TYPES)}
                             onChange={onDocumentTypesChange}
                             options={getDocumentTypeOptions(filter.documentTypes)}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <TextField label={t(FormSessionTranslKey.USER)}
                           value={filter.person?.nameLike}
                           onChange={onUserNameChange}/>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <TextField label={t(FormSessionTranslKey.EMAIL)}
                           value={filter.person?.emailLike}
                           onChange={onEmailChange}/>
            </Grid>
        </Grid>
    );
}
